/**
 * A 360° photo's six cube faces, made on the CPU (ADR-036, raised to 5760 px
 * on 30 Sep 2026). The pure maths lives in `@vrlearning/pano-maths` (ADR-041),
 * shared with the media worker, and is re-exported here; this file adds the
 * decoding. No three.js, so the pano worker (pano-worker.ts) carries only this.
 *
 * The photo never goes up as a 2D texture: at 5760 × 2880 that alone is
 * 66 MB, and with the cube beside it a change would pass the 96 MB texture
 * budget. The worker decodes the JPEG, samples it into the six faces here and
 * hands back their bytes; only the faces are uploaded (render/pano.ts).
 */

import { cubeFace, panoFaces } from '@vrlearning/pano-maths';

export {
  PANO_FACE_MAX,
  cubeFace,
  faceDirection,
  panoFaces,
  sampleEquirect,
} from '@vrlearning/pano-maths';

/** The six faces, WebGL's order (+x, −x, +y, −y, +z, −z), RGBA, first row first. */
export type PanoFaces = {
  readonly size: number;
  readonly data: readonly Uint8Array[];
  /** The photo's own size. */
  readonly width: number;
  readonly height: number;
  /** What it cost the worker, ms: decoding and reading, and sampling. */
  readonly ms: { readonly decode: number; readonly reproject: number };
};

/**
 * Decodes a photo and makes its faces, at most `maxFace` on a side (the GPU's
 * `maxCubemapSize`): in the pano worker, or on the page where there is none.
 * The canvas is a CPU one (`willReadFrequently`), so the full photo never
 * takes GPU memory; the bitmap and the canvas go as soon as they are read.
 */
export async function facesFromBlob(blob: Blob, maxFace: number): Promise<PanoFaces> {
  const started = performance.now();
  const bitmap = await createImageBitmap(blob, { premultiplyAlpha: 'none' });
  const { width, height } = bitmap;
  const canvas = new OffscreenCanvas(width, height);
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    bitmap.close();
    throw new Error('no 2d context');
  }
  context.drawImage(bitmap, 0, 0);
  bitmap.close();
  const pixels = context.getImageData(0, 0, width, height).data;
  canvas.width = 1;
  canvas.height = 1;
  const decoded = performance.now();
  const size = Math.min(cubeFace(width), maxFace);
  const data = panoFaces(pixels, width, height, size);
  return {
    size,
    data,
    width,
    height,
    ms: { decode: decoded - started, reproject: performance.now() - decoded },
  };
}
